import { useState, useEffect, ReactNode } from 'react';
import { Text as Paragraph } from '../Typography';
import { Card } from '../Cards';
import {
  SubCategoryList,
  SecondaryDrawerWrapper,
  SubLink,
  SecondarySubNav,
  CardContainer,
  cardContainerStyle,
  secondaryLinkStyle,
  openSecondaryDrawerStyle,
  SecondaryNavWrapper,
} from './styles';
import type { IParsedCardsData, ISubNavigation } from './types';
import { NextLink } from 'components/NextLink';
import { LinksService } from 'service/links/linksService';
import { useGlobalContext } from 'components/Context/hooks/useGlobalContext';

export const SecondaryDrawer = ({
  open = false,
  subCategoryNavData,
  dataTrackingSecNav = '',
  fullData,
  parentCategorySlug,
  trackingEvents,
  onLinkClick,
}: {
  open?: boolean;
  subCategoryNavData?: {
    subCategoryLink: ISubNavigation[];
    parentLink: string;
  };
  dataTrackingSecNav?: string;
  fullData: any;
  parentCategorySlug: string;
  trackingEvents?: {
    tertiary?: (mainNavItem: string, secondaryNavItem: string, tertiaryNavItem: string) => void;
  };
  onLinkClick: () => void;
}): ReactNode => {
  const { pathTranslations } = useGlobalContext();
  const [mainWrapper, setMainWrapper] = useState<any>();
  const [closeDiv, setcloseDiv] = useState<any>();

  const mainNavItem = fullData?.key;
  const secondaryNavItem = dataTrackingSecNav || '';

  useEffect(() => {
    const mainWrap = document.getElementById('secondaryNavWarp');
    const closeMainNav = document.getElementById('close-main-header');
    setcloseDiv(closeMainNav);
    setMainWrapper(mainWrap);
  });

  useEffect(() => {
    // Add a small delay to ensure the DOM is fully rendered
    setTimeout(() => {
      const firstNavigation = document.getElementById('second-category-nav');

      if (firstNavigation && open) {
        firstNavigation.focus();
      }
    }, 600);
  }, [open, parentCategorySlug]);

  const SecondaryNavKeyDown = () => {
    mainWrapper.addEventListener('keyup', (event: any) => {
      if (event.key === 'Escape') {
        closeDiv.focus();
      }
    });
  };

  const customUrl =
    LinksService.getInternalLinks({
      item: fullData?.navCard?.link,
      pathTranslations,
      parentLink: fullData?.navCard?.parentPage,
    }) || fullData?.navCard?.link?.url;

  const parseCardData: IParsedCardsData = {
    id: fullData?.navCard?.cardId,
    title: fullData?.navCard?.title,
    description: fullData?.navCard?.description,
    categoryId: fullData?.navCard?.categoryId,
    category: fullData?.navCard?.parentCategory,
    subCategory: fullData?.navCard?.category,
    image: {
      desktop: fullData?.navCard?.image[0]?.asset?.[0]?.desktop || '',
      mobile: fullData?.navCard?.image[0]?.asset?.[0]?.mobile || '',
      mimeType: fullData?.navCard?.image[0]?.asset?.[0]?.mimeType,
    },
    video: fullData?.navCard?.video,
    information: {
      preparationTime: fullData?.navCard?.preparationTime,
      rating: fullData?.navCard?.rating,
      difficulty: 0,
      totalTime: fullData?.navCard?.preparationTime,
    },
    link: {
      url: customUrl,
      displayName: fullData?.navCard?.linkTitle,
      openInNewTab: fullData?.navCard?.openInNewTab,
    },
  };

  const difficultyLevel = {
    0: fullData?.navCard?.difficulty,
  };

  if (!open) {
    return null;
  }

  return (
    <SecondaryDrawerWrapper
      id="secondaryNavWarp"
      onKeyDown={SecondaryNavKeyDown}
      className={open && subCategoryNavData?.subCategoryLink ? openSecondaryDrawerStyle : ''}
    >
      <SecondaryNavWrapper>
        <SecondarySubNav className="mx-20P my-10P">
          <ul className="mb-30 border-b border-heroCurve">
            {subCategoryNavData?.subCategoryLink?.map((item: any, index: number) => (
              <SubCategoryList className="subCategory-navigation-list" key={item.id}>
                <SubLink tag="div" type="xxxs" className={secondaryLinkStyle}>
                  <NextLink
                    className="secondary-link focus-visible:outline-1"
                    href={
                      LinksService.getInternalLinks({
                        item,
                        pathTranslations,
                        parentLink: subCategoryNavData?.parentLink,
                      }) || item.url
                    }
                    tabIndex={0}
                    target={item.openInNewTab ? '_blank' : '_self'}
                    onClick={() => {
                      trackingEvents?.tertiary &&
                        trackingEvents?.tertiary(mainNavItem, secondaryNavItem, item.displayName);
                      onLinkClick();
                    }}
                    id={index === 0 ? 'second-category-nav' : ''}
                    onBlur={() => {
                      if (subCategoryNavData?.subCategoryLink?.length === index + 1 && !fullData) {
                        onLinkClick();
                      }
                    }}
                    area-label={item.displayName}
                  >
                    {item.displayName}
                  </NextLink>
                </SubLink>
              </SubCategoryList>
            ))}
          </ul>
          <div className="card-container">
            {!!fullData && (
              <>
                <Paragraph tag="div" type="lead" className="mb-5">
                  {fullData?.cardComponentTitle}
                </Paragraph>
                <CardContainer className={cardContainerStyle}>
                  <Card
                    data={parseCardData}
                    showInfo={fullData?.key === 'Recipes'}
                    link={parseCardData.link}
                    height="350px"
                    video={fullData?.key === 'Recipes'}
                    cardType="small"
                    cardVariation={fullData?.navCard?.cardVariation || 'recipe'}
                    copyDictionary={difficultyLevel}
                    position={1}
                    onBlur={onLinkClick}
                  />
                </CardContainer>
              </>
            )}
          </div>
        </SecondarySubNav>
      </SecondaryNavWrapper>
    </SecondaryDrawerWrapper>
  );
};
